import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import { Select } from '../ui/Select.js';
import { Checkbox } from '../ui/Checkbox.js';
import { useCategories, useRelationshipCategories, useContactTagsList } from '../../hooks/useContacts.js';
import { api } from '../../lib/api.js';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  onClose: () => void;
}

type Step = 'upload' | 'map' | 'categorize' | 'preview' | 'done';

const CONTACT_FIELDS = [
  { key: 'firstName', label: 'Nombre' },
  { key: 'lastName', label: 'Apellido' },
  { key: 'primaryEmail', label: 'Email' },
  { key: 'primaryPhone', label: 'Teléfono' },
  { key: 'businessName', label: 'Empresa' },
  { key: 'notes', label: 'Notas' },
];

function parseCsv(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = lines[0]!.split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  const rows = lines.slice(1).map(line => {
    const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    return Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? '']));
  });
  return { headers, rows };
}

export function ContactImportWizard({ onClose }: Props) {
  const { t } = useTranslation();
  const { data: categories } = useCategories();
  const { data: relCategories = [] } = useRelationshipCategories();
  const { data: allTags = [] } = useContactTagsList();
  const qc = useQueryClient();

  const [step, setStep] = useState<Step>('upload');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [fieldMap, setFieldMap] = useState<Record<string, string>>({});
  const [defaultCategory, setDefaultCategory] = useState('personal');
  const [importRelCategory, setImportRelCategory] = useState('');
  const [importTagIds, setImportTagIds] = useState<number[]>([]);
  const [result, setResult] = useState<{ total: number; created: number; skipped: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const { headers, rows } = parseCsv(e.target?.result as string);
        if (headers.length === 0) { setError('CSV vacío o inválido'); return; }
        setCsvHeaders(headers);
        setCsvRows(rows);
        // Auto-map obvious headers
        const autoMap: Record<string, string> = {};
        for (const h of headers) {
          const lower = h.toLowerCase();
          if (/first.?name|nombre/i.test(lower)) autoMap['firstName'] = h;
          else if (/last.?name|apellido/i.test(lower)) autoMap['lastName'] = h;
          else if (/email|correo/i.test(lower)) autoMap['primaryEmail'] = h;
          else if (/phone|tel[eé]fono|m[oó]vil/i.test(lower)) autoMap['primaryPhone'] = h;
          else if (/company|empresa|negocio/i.test(lower)) autoMap['businessName'] = h;
          else if (/notes?|notas?/i.test(lower)) autoMap['notes'] = h;
        }
        setFieldMap(autoMap);
        setError('');
        setStep('map');
      } catch {
        setError('Error al leer el archivo');
      }
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleImport = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post<{ total: number; created: number; skipped: number }>(
        '/api/contacts/import/csv',
        {
          rows: csvRows, fieldMap, defaultCategory,
          ...(importRelCategory ? { relationshipCategory: importRelCategory } : {}),
          ...(importTagIds.length > 0 ? { tagIds: importTagIds } : {}),
        },
      );
      setResult(res);
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact-categories'] });
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al importar');
    } finally {
      setLoading(false);
    }
  };

  const previewRows = csvRows.slice(0, 5);
  const mappedFields = CONTACT_FIELDS.filter(f => fieldMap[f.key]);

  return (
    <>
      {/* DS-DEFERRED: custom modal shell, port to Modal DS pending */}
      <div className="fixed inset-0 z-40" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ pointerEvents: 'none' }}
      >
        <div
          className="flex flex-col rounded-xl overflow-hidden"
          style={{
            width: 600,
            maxHeight: '85vh',
            background: 'var(--evo-bg-3)',
            border: '1px solid var(--evo-stroke)',
            pointerEvents: 'auto',
            animation: 'overlay-enter 0.3s var(--ease-spring) both',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4 shrink-0"
            style={{ borderBottom: '1px solid var(--evo-stroke)' }}
          >
            <h2 className="font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              {t('contacts.importCsv')}
            </h2>
            <IconButton icon={<Icon name="close" size={14} />} ariaLabel="close" onClick={onClose} variant="ghost" tone="muted" size="sm" />
          </div>

          {/* Step indicator */}
          <div className="flex gap-0 shrink-0" style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
            {(['upload', 'map', 'categorize', 'preview', 'done'] as Step[]).map((s, i) => (
              <div
                key={s}
                className="flex-1 py-2 text-center text-xs"
                style={{
                  color: step === s ? 'var(--evo-cyan)' : 'var(--evo-fg-3)',
                  borderBottom: step === s ? '2px solid var(--evo-cyan)' : '2px solid transparent',
                }}
              >
                {i + 1}. {s === 'upload' ? 'Archivo' : s === 'map' ? 'Mapear' : s === 'categorize' ? t('contacts.import.step_relationship') : s === 'preview' ? 'Vista previa' : 'Listo'}
              </div>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Step 1: Upload */}
            {step === 'upload' && (
              <div className="flex flex-col items-center gap-4">
                <div
                  className="w-full rounded-xl flex flex-col items-center justify-center gap-3 cursor-pointer"
                  style={{
                    border: '2px dashed var(--evo-stroke)',
                    padding: '48px 24px',
                  }}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) handleFile(file);
                  }}
                >
                  <span className="text-3xl">📄</span>
                  <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>
                    Arrastra un CSV aquí o haz clic para seleccionar
                  </p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                  />
                </div>
                {error && <p className="text-sm" style={{ color: 'var(--evo-danger)' }}>{error}</p>}
              </div>
            )}

            {/* Step 2: Map fields */}
            {step === 'map' && (
              <div className="flex flex-col gap-4">
                <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>
                  {csvRows.length.toLocaleString()} filas detectadas. Mapea las columnas del CSV a los campos del contacto.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {CONTACT_FIELDS.map(f => (
                    <div key={f.key}>
                      <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{f.label}</label>
                      <Select<string>
                        size="sm"
                        fullWidth
                        value={fieldMap[f.key] ?? ''}
                        options={[
                          { value: '', label: '— omitir —' },
                          ...csvHeaders.map(h => ({ value: h, label: h })),
                        ]}
                        onChange={v => setFieldMap(m => ({ ...m, [f.key]: v ?? '' }))}
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>
                    Categoría por defecto
                  </label>
                  <Select<string>
                    size="sm"
                    fullWidth
                    value={defaultCategory}
                    options={[
                      ...(categories?.map(c => ({ value: c.category, label: t(`categories.${c.category}`, { defaultValue: c.category }) })) ?? []),
                      { value: 'personal', label: t('contacts.category.personal', { defaultValue: 'Personal' }) },
                      { value: 'client', label: t('contacts.category.client', { defaultValue: 'Clients' }) },
                      { value: 'prospect', label: t('contacts.category.prospect', { defaultValue: 'Prospects' }) },
                    ]}
                    onChange={v => { if (v != null) setDefaultCategory(v); }}
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="solid" tone="cyan" size="sm" icon={<Icon name="arrow-right" size={14} />} iconAfter={undefined} onClick={() => setStep('categorize')}>
                    {t('contacts.import.step_relationship')}
                  </Button>
                  <Button variant="outline" tone="muted" size="sm" onClick={() => setStep('upload')}>← Atrás</Button>
                </div>
              </div>
            )}

            {/* Step 3: Categorize */}
            {step === 'categorize' && (
              <div className="flex flex-col gap-4">
                <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>
                  {t('contacts.import.category_hint')}
                </p>
                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>
                    {t('contacts.import.step_relationship')}
                  </label>
                  <Select<string>
                    size="sm"
                    fullWidth
                    value={importRelCategory}
                    options={[
                      { value: '', label: t('contacts.import.no_category') },
                      ...relCategories.map(r => ({ value: r.slug, label: r.labelEs })),
                    ]}
                    onChange={v => setImportRelCategory(v ?? '')}
                  />
                </div>

                {allTags.length > 0 && (
                  <div>
                    <label className="block text-xs mb-2" style={{ color: 'var(--evo-fg-3)' }}>
                      {t('contacts.import.tags_hint')}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {allTags.map(tag => (
                        <div
                          key={tag.id}
                          className="flex items-center gap-1.5 text-xs cursor-pointer"
                          onClick={() => setImportTagIds(prev =>
                            prev.includes(tag.id) ? prev.filter(id => id !== tag.id) : [...prev, tag.id]
                          )}
                        >
                          <div onClick={e => e.stopPropagation()}>
                            <Checkbox
                              checked={importTagIds.includes(tag.id)}
                              onChange={checked => setImportTagIds(prev =>
                                checked ? [...prev, tag.id] : prev.filter(id => id !== tag.id)
                              )}
                            />
                          </div>
                          <span
                            className="px-2 py-0.5 rounded-full"
                            style={{
                              background: tag.color ? `${tag.color}22` : 'var(--evo-bg-2)',
                              color: tag.color ?? 'var(--evo-fg-3)',
                              border: `1px solid ${tag.color ? `${tag.color}66` : 'var(--evo-stroke)'}`,
                            }}
                          >
                            {tag.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button variant="solid" tone="cyan" size="sm" iconAfter={<Icon name="arrow-right" size={14} />} onClick={() => setStep('preview')}>Vista previa</Button>
                  <Button variant="outline" tone="muted" size="sm" onClick={() => setStep('map')}>← Atrás</Button>
                </div>
              </div>
            )}

            {/* Step 4: Preview */}
            {step === 'preview' && (
              <div className="flex flex-col gap-4">
                <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>
                  Primeros {previewRows.length} contactos de {csvRows.length.toLocaleString()} a importar:
                </p>

                <div className="overflow-x-auto rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
                  <table className="w-full text-xs">
                    <thead style={{ background: 'var(--evo-bg-2)' }}>
                      <tr>
                        {mappedFields.map(f => (
                          <th key={f.key} className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>
                            {f.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--evo-stroke)' }}>
                          {mappedFields.map(f => (
                            <td key={f.key} className="px-3 py-2">
                              {fieldMap[f.key] ? (row[fieldMap[f.key]!] ?? '') : '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {error && <p className="text-sm" style={{ color: 'var(--evo-danger)' }}>{error}</p>}

                <div className="flex gap-2">
                  <Button variant="solid" tone="cyan" size="sm" disabled={loading} loading={loading} onClick={() => void handleImport()}>
                    {loading ? t('common.loading') : `Importar ${csvRows.length.toLocaleString()} contactos`}
                  </Button>
                  <Button variant="outline" tone="muted" size="sm" onClick={() => setStep('categorize')}>← Atrás</Button>
                </div>
              </div>
            )}

            {/* Step 4: Done */}
            {step === 'done' && result && (
              <div className="flex flex-col items-center gap-4 py-6">
                <span className="text-5xl">✅</span>
                <h3 className="font-semibold text-lg">Importación completa</h3>
                <div className="grid grid-cols-3 gap-4 w-full text-center">
                  {[
                    { label: 'Total procesados', val: result.total },
                    { label: 'Creados', val: result.created },
                    { label: 'Omitidos (dup)', val: result.skipped },
                  ].map(({ label, val }) => (
                    <div key={label} className="p-3 rounded-lg" style={{ background: 'var(--evo-bg-2)' }}>
                      <p className="text-2xl font-bold" style={{ color: 'var(--evo-cyan)' }}>{val}</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--evo-fg-3)' }}>{label}</p>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '8px' }}>
                  <Button variant="solid" tone="cyan" onClick={onClose}>Cerrar</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
